import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SourceContext = createContext(null);

// Default neutral source config (used briefly during initial load before real source data arrives from API)
const DEFAULT_SOURCE = {
  slug: null,
  name: 'ApplyDrive',
  company_name: 'ApplyDrive',
  logo_url: null,
  primary_color: '#00a94f',
  contact_email: null,
  contact_phone: null,
  privacy_policy_url: null,
  idd_url: null,
  landing_image_url: null,
};

export const SourceProvider = ({ slug, children }) => {
  const [source, setSource] = useState(DEFAULT_SOURCE);
  const [loading, setLoading] = useState(!!slug);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchSource = async () => {
      // If no slug, load the default carmoney-ce source from DB
      const fetchSlug = slug || 'carmoney-ce';
      try {
        const res = await axios.get(`${API_URL}/source/${fetchSlug}`);
        setSource({ ...DEFAULT_SOURCE, ...res.data });
        setNotFound(false);
      } catch {
        if (slug) {
          setNotFound(true);
        } else {
          // Fallback to hardcoded defaults if DB not available
          setSource(DEFAULT_SOURCE);
        }
      }
      setLoading(false);
    };
    fetchSource();
  }, [slug]);

  // Apply CSS custom properties for primary color + variants
  useEffect(() => {
    const color = source.primary_color || '#00a94f';
    document.documentElement.style.setProperty('--source-primary', color);
    document.documentElement.style.setProperty('--source-primary-hover', `color-mix(in srgb, ${color} 80%, black)`);
    document.documentElement.style.setProperty('--source-primary-light', `color-mix(in srgb, ${color} 8%, white)`);
    document.documentElement.style.setProperty('--source-primary-medium', `color-mix(in srgb, ${color} 15%, white)`);
    document.documentElement.style.setProperty('--source-primary-ring', `color-mix(in srgb, ${color} 25%, white)`);
    // Override shadcn CSS variables so outline buttons, focus rings, etc. match the source brand
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    document.documentElement.style.setProperty('--primary', hsl);
    document.documentElement.style.setProperty('--ring', hsl);
    document.documentElement.style.setProperty('--accent', hsl);
  }, [source.primary_color]);

  const getSubmitUrl = () => {
    const s = source.slug || 'carmoney-ce';
    const broker = (source.broker_system || 'autoconvert').toLowerCase();
    if (broker === 'artis') {
      return `${API_URL}/submit-to-artis/${s}`;
    }
    return `${API_URL}/submit-to-autoconvert/${s}`;
  };

  return (
    <SourceContext.Provider value={{ source, loading, notFound, getSubmitUrl }}>
      {children}
    </SourceContext.Provider>
  );
};

export const useSource = () => {
  const ctx = useContext(SourceContext);
  if (!ctx) return { source: DEFAULT_SOURCE, loading: false, notFound: false, getSubmitUrl: () => `${API_URL}/submit-to-autoconvert` };
  return ctx;
};
